"use client";

import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Build identifier, baked into the client bundle at `next build` time
 * (see `NEXT_PUBLIC_BUILD_ID` in next.config.ts). Changes on every deploy.
 */
const BUILD_ID = process.env.NEXT_PUBLIC_BUILD_ID ?? "";

/** localStorage key holding the build the user last acknowledged. */
const STORAGE_KEY = "synoplix:lastSeenBuild";

/** Format the ISO build id as a readable French date/time. */
function formatBuildTime(buildId: string): string {
  const d = new Date(buildId);
  if (Number.isNaN(d.getTime())) return buildId;
  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Shows a one-time modal after a new version has been deployed.
 *
 * On load it compares the build baked into the running bundle with the
 * one stored in localStorage. If they differ, the modal opens once;
 * dismissing it records the new build so it never reopens for that build.
 *
 * Restricted to the dev environment (dev.synoplix.com + localhost) — it
 * stays silent in production.
 */
export function UpdateNotification() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!BUILD_ID) return;

    // Dev environment only — never notify end users on production.
    const host = window.location.hostname;
    const isDev =
      host.startsWith("dev.") || host === "localhost" || host === "127.0.0.1";
    if (!isDev) return;

    let lastSeen: string | null = null;
    try {
      lastSeen = localStorage.getItem(STORAGE_KEY);
    } catch {
      return; // localStorage unavailable — skip silently
    }

    if (lastSeen === null) {
      // First visit on this browser: nothing to announce, just record it.
      try {
        localStorage.setItem(STORAGE_KEY, BUILD_ID);
      } catch {
        /* ignore */
      }
      return;
    }

    // A different build than the one last acknowledged → notify once.
    if (lastSeen !== BUILD_ID) {
      setOpen(true);
    }
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, BUILD_ID);
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Any close (button, overlay, Escape) marks this build as seen.
        if (!next) dismiss();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="size-5 text-primary" />
            Mise à jour déployée
          </DialogTitle>
          <DialogDescription>
            Une nouvelle version de Synoplix vient d&apos;être déployée sur
            l&apos;environnement de développement. Pense à vérifier les
            changements.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Build&nbsp;: {formatBuildTime(BUILD_ID)}
        </p>
        <DialogFooter>
          <Button onClick={dismiss}>Compris</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
